import React, { useState, useEffect, useRef } from "react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import styles from "./styles.module.scss";

const DraggableFieldSelector = ({
    selectedFields = [],
    selectableItems = [],
    onSelectionChanged,
    dataTestId = "field-selector",
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [availableItems, setAvailableItems] = useState([]);
    // State for currently dragging item
    const [activeId, setActiveId] = useState(null);
    // Ref to handle auto-scrolling when dragging in an overflowing box
    const leftBoxRef = useRef(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        const selected = selectedFields.map((field) =>
            selectableItems.find((item) => item.value === field)
        );

        const available = selectableItems.filter(
            (item) => !selectedFields.includes(item.value)
        );

        setSelectedItems(selected);
        setAvailableItems(available);
    }, [selectedFields, selectableItems]);

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveId(active.id);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            setSelectedItems((items) => {
                const oldIndex = items.findIndex(
                    (item) => item.value === active.id
                );
                const newIndex = items.findIndex(
                    (item) => item.value === over.id
                );

                const newItems = arrayMove(items, oldIndex, newIndex);

                onSelectionChanged(newItems.map((item) => item.value));

                return newItems;
            });
        }

        setActiveId(null);
    };

    const handleAddItem = (item) => {
        const newSelectedItems = [item, ...selectedItems];
        const newAvailableItems = availableItems.filter(
            (i) => i.value !== item.value
        );

        setSelectedItems(newSelectedItems);
        setAvailableItems(newAvailableItems);

        onSelectionChanged(newSelectedItems.map((item) => item.value));
    };

    const handleRemoveItem = (item) => {
        const newSelectedItems = selectedItems.filter(
            (i) => i.value !== item.value
        );

        const allItemsIndex = selectableItems.findIndex(
            (i) => i.value === item.value
        );

        const newAvailableItems = [...availableItems];

        let insertIndex = newAvailableItems.findIndex((availItem) => {
            const availItemIndex = selectableItems.findIndex(
                (i) => i.value === availItem.value
            );
            return availItemIndex > allItemsIndex;
        });

        // If no position found (should be at the end), set to the end of the array
        if (insertIndex === -1) {
            insertIndex = newAvailableItems.length;
        }

        newAvailableItems.splice(insertIndex, 0, item);

        setSelectedItems(newSelectedItems);
        setAvailableItems(newAvailableItems);

        onSelectionChanged(newSelectedItems.map((item) => item.value));
    };

    // Find the currently active item
    const getActiveItem = () => {
        return selectedItems.find((item) => item.value === activeId);
    };

    return (
        <div className={styles.container}>
            <div className={styles.titleContainer}>
                <div>Configured Fields</div>
                <div>Fields</div>
            </div>
            <div className={styles.boxContainer}>
                {/* Left Box - Selected Items */}
                <div
                    ref={leftBoxRef}
                    className={`${styles.box} ${styles.left}`}
                    data-testid={`${dataTestId}-selected`}
                >
                    <div className={styles.boxHeader}>
                        <div className={styles.leftPadding}></div>
                        <div className={styles.boxTitle}>Field Name</div>
                        <div className={styles.actionHeader}>Delete</div>
                    </div>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        modifiers={[restrictToVerticalAxis]}
                    >
                        <SortableContext
                            items={selectedItems.map((item) => item.value)}
                            strategy={verticalListSortingStrategy}
                        >
                            <div className={styles.itemList}>
                                {selectedItems.map((item) => (
                                    <SortableItem
                                        key={item.value}
                                        id={item.value}
                                        item={item}
                                        onRemove={handleRemoveItem}
                                        isActive={activeId === item.value}
                                    />
                                ))}
                            </div>
                        </SortableContext>

                        {/* Drag Overlay */}
                        <DragOverlay adjustScale={true}>
                            {activeId ? (
                                <ItemOverlay item={getActiveItem()} />
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </div>

                {/* Right Box - Available Items */}
                <div
                    className={`${styles.box} ${styles.right}`}
                    data-testid={`${dataTestId}-available`}
                >
                    <div className={styles.boxHeader}>
                        <div className={styles.boxTitle}>Field Name</div>
                        <div className={styles.actionHeader}>Add</div>
                    </div>
                    <div className={styles.itemList}>
                        {availableItems.map((item) => (
                            <div key={item.value} className={styles.item}>
                                <div className={styles.itemName}>
                                    {item.name}
                                </div>
                                <div className={styles.buttonArea}>
                                    <button
                                        className={styles.addButton}
                                        onClick={() => handleAddItem(item)}
                                        data-testid={`add-item-${item.value}`}
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SortableItem = ({ id, item, onRemove, isActive }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
            style={style}
        >
            <div
                className={styles.dragHandle}
                {...attributes}
                {...listeners}
                data-testid={`drag-item-${id}`}
            ></div>
            <div className={styles.itemName}>{item.name}</div>
            <div className={styles.buttonArea}>
                <button
                    className={styles.deleteButton}
                    onClick={() => onRemove(item)}
                    data-testid={`remove-item-${id}`}
                >
                    –
                </button>
            </div>
        </div>
    );
};

const ItemOverlay = ({ item }) => {
    if (!item) return null;

    return (
        <div className={`${styles.item} ${styles.itemOverlay}`}>
            <div className={styles.dragHandle}>≡</div>
            <div className={styles.itemName}>{item.name}</div>
        </div>
    );
};

export default DraggableFieldSelector;
